import React, { useEffect, useMemo, useState } from "react"

import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { StepperElement } from "@front-shared/elements/stepper.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { intakeDemoActionCreators } from "../../actions/intakeDemo.actions"
import { GENERAL_CONFIG } from "../../config/general.config"
import { IntakeDemoFlow } from "../../config/intakeDemo.config"
import { STEPPER_CONFIG } from "../../config/stepper.config"
import { intakeDemoSelector } from "../../selectors/intakeDemo.selector"
import type {
    IGetCoverageInitialData,
    IGetCoveragePayerData,
} from "../../types/data.types.ts"
import { IntakeDemoAuthComponent } from "./intakeDemoAuth.component"
import { IntakeDemoFailureComponent } from "./intakeDemoFailure.component"
import { IntakeDemoHeaderComponent } from "./intakeDemoHeader.component"
import { IntakeDemoSuccessComponent } from "./intakeDemoSuccess.component"

export const IntakeDemoComponent: React.FunctionComponent = () => {
    useScrollFix()

    const dispatch = useDispatch()
    const [searchParams] = useSearchParams()

    const intakeDemoState = useSelector(intakeDemoSelector)

    // Checkout session id that was successful
    const [demoLogo] = useState<string | null>(
        searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.logo),
    )

    const [intakeDemoInitialData, setIntakeDemoInitialData] =
        useState<IGetCoverageInitialData>()

    // On mount check if already authenticated
    // In this case validate that authentication is valid
    useEffect(() => {
        if (!intakeDemoState.isAuthenticated) {
            return
        }

        const token = BrowserStorageHelper.get<string>(
            GENERAL_CONFIG.browserStorageKeys.tokenKey,
            undefined,
            BrowserStorageType.sessionStorage,
        )

        if (!token) {
            return
        }

        dispatch(intakeDemoActionCreators.authStart(token))
    }, [])

    const getSpecialStepBottomContent = (
        currentStepIndex: number,
    ): React.JSX.Element | null => {
        return currentStepIndex === 0 ? (
            <div className="fixed bottom-[24px] z-10 horizontal-center">
                <img
                    src={`${import.meta.env.VITE_S3_ASSETS_BASE_URL}/intake-demo-logos/${
                        demoLogo || "default.png"
                    }`}
                    width={250}
                    alt="Demo logo"
                />
            </div>
        ) : null
    }

    const onInitialStepperSubmit = (data: IGetCoverageInitialData) => {
        setIntakeDemoInitialData(data)

        dispatch(intakeDemoActionCreators.getCoverage(data))
    }

    const onPayerStepperSubmit = (data: IGetCoveragePayerData) => {
        if (!intakeDemoInitialData) {
            dispatch(
                intakeDemoActionCreators.setCurrentFlow(
                    IntakeDemoFlow.initialData,
                ),
            )

            return
        }

        dispatch(
            intakeDemoActionCreators.getCoverage(intakeDemoInitialData, data),
        )
    }

    const currentFlowContent = useMemo<React.JSX.Element>(() => {
        switch (intakeDemoState.currentFlow) {
            case IntakeDemoFlow.failure:
                return <IntakeDemoFailureComponent />

            case IntakeDemoFlow.success:
                return <IntakeDemoSuccessComponent />

            case IntakeDemoFlow.payerData:
                return (
                    <StepperElement
                        key="intakeDemoPayer"
                        steps={STEPPER_CONFIG.intakeDemoPayer}
                        onStepperSubmit={onPayerStepperSubmit}
                    />
                )

            case IntakeDemoFlow.initialData:
            default:
                return (
                    <StepperElement
                        key="intakeDemoInitial"
                        steps={STEPPER_CONFIG.intakeDemoInitial}
                        onStepperSubmit={onInitialStepperSubmit}
                        getSpecialStepBottomContent={
                            getSpecialStepBottomContent
                        }
                    />
                )
        }
    }, [intakeDemoState.currentFlow])

    return (
        <div>
            <PageHelmetElement
                title="Intake Demo"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <IntakeDemoHeaderComponent />

            <div className="px-[24px]">
                {!intakeDemoState.isAuthenticated ? (
                    <IntakeDemoAuthComponent />
                ) : (
                    currentFlowContent
                )}
            </div>
        </div>
    )
}
